// src/pages/super-admin/Tests/CreateTest.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useCreateTestTypeMutation,
  type TestTypeRequest,
} from "@/features/tests/services/testApi";
import { useListTestCategoriesQuery } from "@/api/masterDataApi";
import { toast } from "sonner";

interface CreateTestProps {
  onSuccess: (test: any) => void;
  onClose: () => void;
}

export default function CreateTest({ onSuccess, onClose }: CreateTestProps) {
  // Use camelCase for form data
  const [formData, setFormData] = useState<TestTypeRequest>({
    testName: "",
    testDescription: "",
    testCategory: "",
    status: "ACTIVE",
  });

  const [createTest, { isLoading }] = useCreateTestTypeMutation();
  const { data: categories, isLoading: catLoading } = useListTestCategoriesQuery();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.testName.trim() || !formData.testCategory.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Log to verify payload before sending
      console.log("Sending payload:", formData);

      const result = await createTest(formData).unwrap();
      toast.success("Test type created successfully");
      onSuccess(result);
      onClose();
    } catch (err) {
      console.error("Create test error:", err);
      toast.error("Failed to create test type");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Test Name */}
      <div>
        <label className="block text-sm font-medium mb-2">Test Name *</label>
        <Input
          name="testName"
          placeholder="e.g., Blood Group Test"
          value={formData.testName}
          onChange={handleInputChange}
          disabled={isLoading}
          required
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium mb-2">Category *</label>
        <select
          name="testCategory"
          value={formData.testCategory}
          onChange={handleSelectChange}
          disabled={isLoading || catLoading}
          required
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select Category</option>
          {categories?.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-2">Description</label>
        <textarea
          name="testDescription"
          placeholder="Detailed description of the test"
          value={formData.testDescription}
          onChange={handleTextAreaChange}
          disabled={isLoading}
          className="w-full border border-gray-300 rounded px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium mb-2">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleSelectChange}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isLoading}
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end pt-4">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600" disabled={isLoading}>
          {isLoading ? "Creating..." : "Create Test"}
        </Button>
      </div>
    </form>
  );
}
